import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'
import { defaultSubpaths } from './default-subpaths'

export function serializeSubpath(points: PenAnchorPoint[], isClosed: boolean): string {
  const p0 = points[0]
  let d = `M ${fmt(p0.x)} ${fmt(p0.y)}`

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    const cp1x = prev.handleOut?.x ?? prev.x
    const cp1y = prev.handleOut?.y ?? prev.y
    const cp2x = curr.handleIn?.x ?? curr.x
    const cp2y = curr.handleIn?.y ?? curr.y

    if (prev.handleOut || curr.handleIn) {
      d += ` C ${fmt(cp1x)} ${fmt(cp1y)} ${fmt(cp2x)} ${fmt(cp2y)} ${fmt(curr.x)} ${fmt(curr.y)}`
    } else {
      d += ` L ${fmt(curr.x)} ${fmt(curr.y)}`
    }
  }

  if (isClosed) {
    // A closing curve segment needs two distinct points to connect — but `isClosed` alone (not
    // point count) determines whether this subpath emits `Z` at all. A degenerate subpath (e.g.
    // a near-duplicate closing point already merged into its first point by `finalizeSubpath`,
    // leaving just one point) still IS closed; omitting `Z` for it silently produces an unclosed
    // path string. That was invisible for a single-contour path (nothing downstream reads `Z`
    // one contour at a time), but for a compound path — several glyph outlines merged into one
    // shape for text-to-shape — a degenerate LAST subpath dropped the whole string's trailing
    // `Z`, which `isShapeTextSupported`'s "is this closed" check reads from the very end of the
    // string. That silently blocked entering shape-text edit on an otherwise normal converted
    // shape, for as long as it stayed in memory (a reload rebuilds the path from `pathCommands`
    // via a different, unaffected code path, which is why the same shape "fixed itself").
    if (points.length >= 2) {
      const last = points[points.length - 1]
      const cp1x = last.handleOut?.x ?? last.x
      const cp1y = last.handleOut?.y ?? last.y
      const cp2x = p0.handleIn?.x ?? p0.x
      const cp2y = p0.handleIn?.y ?? p0.y

      if (last.handleOut || p0.handleIn) {
        d += ` C ${fmt(cp1x)} ${fmt(cp1y)} ${fmt(cp2x)} ${fmt(cp2y)} ${fmt(p0.x)} ${fmt(p0.y)}`
      }
    }

    d += ' Z'
  }

  return d
}

/**
 * Convert PenAnchorPoint[] back to an SVG path string in the same local coordinate space.
 * Pass `subpaths` (from `parseSvgPathToAnchors`) for a compound path so each contour gets its own
 * M...Z fragment — omit it (or pass a single range) for the traditional single-contour case,
 * where `isClosed` alone still fully determines the output exactly as before.
 */
export function anchorsToSvgPath(
  points: PenAnchorPoint[],
  isClosed: boolean,
  subpaths?: PenSubpathRange[]
): string {
  if (points.length === 0) return ''

  const ranges = defaultSubpaths(points, isClosed, subpaths)
  const parts: string[] = []

  for (const range of ranges) {
    const rangePoints = points.slice(range.start, range.end)

    if (rangePoints.length === 0) continue
    parts.push(serializeSubpath(rangePoints, range.isClosed))
  }

  return parts.join(' ')
}

export function fmt(n: number): string {
  return parseFloat(n.toFixed(4)).toString()
}
